import React, { useState, useEffect, useContext } from "react";
import PropTypes from "prop-types";
import { useMsal } from "@azure/msal-react";
import { isValid } from "date-fns";
import { Box, Typography, FormControl, FormControlLabel, RadioGroup } from "@mui/material";
import CityAutocomplete from "../../../../Inputs/CityAutocomplete/CityAutocomplete";
import PrimaryDatePicker from "../../../../Inputs/PrimaryDatePicker/PrimaryDatePicker";
import PrimaryTextField from "../../../../Inputs/PrimaryTextField/PrimaryTextField";
import PrimaryRadio from "../../../../Buttons/PrimaryRadio/PrimaryRadio";
import PrimaryButton from "../../../../Buttons/PrimaryButton/PrimaryButton";
import SecondaryButton from "../../../../Buttons/SecondaryButton/SecondaryButton";
import useEquipmentTypes from "../../../../../hooks/useEquipmentTypes/useEquipmentTypes";
import { getDistance } from "../../../../../utils/calcHelpers";
import { CarrierService } from "../../../../../services/CarrierService";
import { AlertContext } from "../../../../Dashboard/Dashboard";

export default function PastePostedTrucksRow({
  index,
  row,
  defaultMode,
  defaultStartDate,
  defaultExpiryDate,
  defaultDestination,
  userIdMcld,
  activeCarrier,
  setErrorLabel,
  submitAllTriggered,
  updateCompletedCount,
  updateErrorBalance,
  updateRemovedBalance
}) {
  const { instance } = useMsal();
  const { warning } = useContext(AlertContext);
  const { typeLabels, typeModes } = useEquipmentTypes();
  const [originCityDefault, setOriginCityDefault] = useState("");
  const [originCity, setOriginCity] = useState({});
  const [destinationCityDefault, setDestinationCityDefault] = useState("");
  const [destinationCity, setDestinationCity] = useState({});
  const [startDate, setStartDate] = useState(new Date(defaultStartDate));
  const [expiryDate, setExpiryDate] = useState(new Date(defaultExpiryDate));
  const [mode, setMode] = useState(defaultMode);
  const [proposedCost, setProposedCost] = useState(null);
  const [ratePerMile, setRatePerMile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [removeLoading, setRemoveLoading] = useState(false);
  const [submitDisabled, setSubmitDisabled] = useState(true);
  const [isError, setIsError] = useState(true);
  const [isRemoved, setIsRemoved] = useState(false);
  const [payload, setPayload] = useState({});

  const handleSubmit = () => {
    setSubmitDisabled(true);
    setIsLoading(true);
    CarrierService.CreatePostedTruck(instance, payload).then(() => {
      setIsLoading(false);
      updateCompletedCount();
    }).catch(error => {
      console.error(error);
      setIsLoading(false);
      warning(error);
    });
  };

  const handleRemove = () => {
    setRemoveLoading(!removeLoading);
    setIsRemoved(!isRemoved);
  };

  const handleModeChange = (event) => {
    setMode(event.target.value);
  };

  const handleStartDateChange = (value) => {
    setStartDate(value);
    if (value) {
      let date = new Date(value.getTime());
      setExpiryDate(new Date(date.setDate(date.getDate() + 1)));
    } else {
      setExpiryDate(value);
    }
  };

  useEffect(() => {
    if (isError) {
      updateErrorBalance(1);
    } else {
      updateErrorBalance(-1);
    }
  }, [isError]);

  useEffect(() => {
    if (isRemoved) {
      updateRemovedBalance(1);
    } else {
      updateRemovedBalance(-1);
    }
  }, [isRemoved]);

  useEffect(() => {
    if (submitAllTriggered && !submitDisabled && !isRemoved) {
      handleSubmit();
    }
  }, [submitAllTriggered]);

  useEffect(() => {
    const isDisabled =
      !activeCarrier ||
      !originCity ||
      !startDate ||
      !expiryDate ||
      !isValid(startDate) ||
      !isValid(expiryDate);

    setSubmitDisabled(isDisabled);
    setIsError(isDisabled);
  }, [
    activeCarrier,
    originCity,
    startDate,
    expiryDate
  ]);

  useEffect(() => {
    const newPayload = {
      carrierId: activeCarrier?.id ?? null,
      originCity: originCity?.name ?? null,
      originState: originCity?.state ?? null,
      originCityId: originCity?.id ?? null,
      destinationCity: destinationCity?.name ?? null,
      destinationState: destinationCity?.state ?? null,
      destinationCityId: destinationCity?.id ?? null,
      startDate: isValid(startDate) && startDate.toISOString(),
      expiryDate: isValid(expiryDate) && expiryDate.toISOString(),
      postedDate: new Date().toISOString(),
      postedBy: userIdMcld,
      dispatcher: userIdMcld,
      equipmentTypeId: mode,
      proposedCost: proposedCost,
      proposedRpm: ratePerMile,
      miles: null
    };

    if (originCity?.id && destinationCity?.id) {
      newPayload.miles =
        getDistance(
          originCity.latitude,
          originCity.longitude,
          destinationCity.latitude,
          destinationCity.longitude
        );
    }

    setPayload(newPayload);
  }, [
    activeCarrier,
    originCity,
    destinationCity,
    mode,
    startDate,
    expiryDate,
    proposedCost,
    ratePerMile
  ]);

  useEffect(() => {
    setRemoveLoading(false);

    if (row.cities?.length > 0 && row.states.length > 0) {
      setOriginCityDefault(row.cities?.[0].concat(", ", row.states[0]));
    } else {
      setOriginCityDefault("");
      setErrorLabel("The information could not be fully parsed");
    }

    if (row.cities?.length > 1 && row.states.length > 1) {
      setDestinationCityDefault(row.cities[1].concat(", ", row.states[1]));
    } else if (defaultDestination && Object.keys(defaultDestination).length != 0) {
      setDestinationCityDefault(defaultDestination.name.concat(", ", defaultDestination.state));
    } else {
      setDestinationCityDefault("");
    }

    if (row.mode) {
      let keys = Object.keys(typeModes).filter(key => typeModes[key] === row.mode);
      keys.forEach(key => {
        let mode = typeLabels.find(({ id }) => id === key);
        if (mode) setMode(mode.id);
      });
    }

    if (row.date) {
      setStartDate(row.date);
      let date = new Date(row.date.getTime());
      let today = new Date();
      today.setHours(0, 0, 0, 0);
      if (date.getTime() < today.getTime()) {
        while (date.getTime() < today.getTime()) {
          date = new Date(date.setFullYear(date.getFullYear() + 1));
        }
      }
      setStartDate(new Date(date));
      setExpiryDate(new Date(date.setDate(date.getDate() + 1)));
    }
  }, [row]);

  return (
    <>
      <Box
        sx={{
          backgroundColor: "black_solid.10",
          padding: "10px",
          margin: "20px 0",
          display: "flex",
          justifyContent: "space-between"
        }}>
        <Box sx={{ display: "flex" }}>
          <Typography
            sx={{
              color: isRemoved ? "black_solid.50" : "primary.60",
              marginRight: "10px"
            }}>
            PASTED ENTRY {index + 1}
          </Typography>
          {isRemoved && <Typography sx={{ color: "error.60" }}>REMOVED</Typography>}
        </Box>
        {isRemoved && !isLoading &&
          <Typography
            onClick={handleRemove}
            sx={{
              color: "primary.60",
              textDecoration: "underline",
              cursor: "pointer"
            }}>
            UNDO
          </Typography>
        }
      </Box>
      {!isRemoved &&
        <>
          <Box sx={{ display: "flex" }}>
            <Typography variant="subhead2" color="black_solid.80" sx={{ margin: "0 267px 0 3px" }}>
              Locations
            </Typography>
            <Typography variant="subhead2" color="black_solid.80" sx={{ margin: "0 189px 0 0" }}>
              Dates
            </Typography>
            <Typography variant="subhead2" color="black_solid.80" sx={{ margin: "0 195px 0 0" }}>
              Costs
            </Typography>
            <Typography variant="subhead2" color="black_solid.80">
              Mode
            </Typography>
          </Box>
          <Box sx={{ display: "flex" }}>
            <Box sx={{ flexDirection: "column" }}>
              <Box sx={{ margin: "20px 20px 50px 0" }}>
                <CityAutocomplete
                  label="Current Location *"
                  updateSelectedCity={setOriginCity}
                  defaultInput={originCityDefault}
                  error={!originCity}
                  disableCounterIncrement
                  disableAutoValue
                />
              </Box>
              <Box>
                <CityAutocomplete
                  label="Destination (optional)"
                  updateSelectedCity={setDestinationCity}
                  defaultInput={destinationCityDefault}
                  disableCounterIncrement
                  disableAutoValue
                />
              </Box>
            </Box>
            <Box sx={{ flexDirection: "column" }}>
              <Box sx={{ margin: "20px 20px 50px 0" }}>
                <PrimaryDatePicker
                  label="Start Date *"
                  value={startDate}
                  handleChange={handleStartDateChange}
                  width="200px"
                  disablePast
                  error={!startDate || !isValid(startDate)}
                />
              </Box>
              <Box>
                <PrimaryDatePicker
                  label="Expiry Date *"
                  value={expiryDate}
                  handleChange={setExpiryDate}
                  width="200px"
                  disablePast
                  error={!expiryDate || !isValid(expiryDate)}
                />
              </Box>
            </Box>
            <Box sx={{ flexDirection: "column" }}>
              <Box sx={{ margin: "20px 20px 50px 0" }}>
                <PrimaryTextField
                  onChange={setProposedCost}
                  value={proposedCost}
                  label="Proposed Cost"
                  isNumber
                  isCurrency
                  width="200px"
                  disabled={!!ratePerMile}
                />
              </Box>
              <Box>
                <PrimaryTextField
                  onChange={setRatePerMile}
                  value={ratePerMile}
                  label="Rate per Mile"
                  isNumber
                  isCurrency
                  width="200px"
                  disabled={!!proposedCost}
                />
              </Box>
            </Box>
            <Box sx={{ margin: "10px 0 0 3px" }}>
              <FormControl>
                <RadioGroup
                  aria-labelledby="carrier-search-radio-buttons"
                  name="row-radio-buttons-group"
                  value={mode}
                  onChange={handleModeChange}
                >
                  {typeLabels.map(type => (
                    <FormControlLabel
                      key={`equipment-type-${type.id}`}
                      value={type.id}
                      control={<PrimaryRadio />}
                      label={<Typography variant="body2" color="black_solid.60">{type.label}</Typography>}
                    />
                  ))}
                </RadioGroup>
              </FormControl>
            </Box>
            <Box sx={{ display: "flex", margin: "110px 30px 0 20px" }}>
              <Box sx={{ marginLeft: "auto", marginRight: "20px" }}>
                <SecondaryButton
                  label="Remove"
                  onClick={handleRemove}
                  disabled={isLoading || removeLoading}
                  isLoading={removeLoading}
                />
              </Box>
              <Box>
                <PrimaryButton
                  label="Post"
                  width="50px"
                  onClick={handleSubmit}
                  isLoading={isLoading}
                  disabled={submitDisabled}
                />
              </Box>
            </Box>
          </Box>
        </>
      }
    </>
  );
}

PastePostedTrucksRow.propTypes = {
  index: PropTypes.number.isRequired,
  row: PropTypes.object.isRequired,
  defaultMode: PropTypes.string,
  defaultStartDate: PropTypes.instanceOf(Date),
  defaultExpiryDate: PropTypes.instanceOf(Date),
  defaultDestination: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object
  ]),
  userIdMcld: PropTypes.string,
  activeCarrier: PropTypes.object,
  setErrorLabel: PropTypes.func.isRequired,
  submitAllTriggered: PropTypes.bool.isRequired,
  updateCompletedCount: PropTypes.func.isRequired,
  updateErrorBalance: PropTypes.func.isRequired,
  updateRemovedBalance: PropTypes.func.isRequired
};